import React from 'react'
import { useState, useEffect } from 'react';
import './DisplaySelectionCategory.scss'
import { FaFolder, FaList, FaChevronDown, FaChevronRight, FaEyeSlash } from 'react-icons/fa';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

type Props = {
    categories: any[];
    selectedCategories: any[];
    setSelectedCategories: (categories: any[]) => void;
}

// Utility to check if a category is selected
const isCategorySelected = (selectedCategories: any[], category: any) => {
  return selectedCategories.some((c) => c.category_id === category.category_id);
};

// Utility to get all ids of a category and its children
const getAllCategoryIds = (category: any): number[] => {
  let ids = [category.category_id];
  if (category.childCategoryList && category.childCategoryList.length > 0) {
    category.childCategoryList.forEach((child: any) => {
      ids = ids.concat(getAllCategoryIds(child));
    });
  }
  return ids;
};

const DisplaySelectionCategory = ({ categories, selectedCategories, setSelectedCategories }: Props) => {
  const [expandedCategories, setExpandedCategories] = useState<{ [key: number]: boolean }>({});

  // Auto-expand categories that have selected children
  useEffect(() => {
    const newExpanded: { [key: number]: boolean } = {};
    
    const checkAndExpand = (cats: any[]) => {
      cats.forEach(cat => {
        if (cat.childCategoryList && cat.childCategoryList.length > 0) {
          const hasSelectedChild = cat.childCategoryList.some((child: any) => 
            isCategorySelected(selectedCategories, child)
          );
          if (hasSelectedChild) {
            newExpanded[cat.category_id] = true;
          }
          checkAndExpand(cat.childCategoryList);
        }
      });
    };
    
    checkAndExpand(categories);
    setExpandedCategories(prev => ({ ...prev, ...newExpanded }));
  }, [selectedCategories, categories]);

  // Find parent category
  const findParentCategory = (categories: any[], childId: number): any | null => {
    for (const cat of categories) {
      if (cat.childCategoryList && cat.childCategoryList.some((child: any) => child.category_id === childId)) {
        return cat;
      }
      if (cat.childCategoryList && cat.childCategoryList.length > 0) {
        const found = findParentCategory(cat.childCategoryList, childId);
        if (found) return found;
      }
    }
    return null;
  };

  // Handle category selection
  const handleCategoryChange = (category: any, checked: boolean) => {
    const allIds = getAllCategoryIds(category);
    if (checked) {
      let newSelected = [
        ...selectedCategories,
        ...allIds
          .filter(id => !selectedCategories.some((c: any) => c.category_id === id))
          .map(id => ({ category_id: id, category_name: '', childCategoryList: [] }))
      ];
      
      // Auto-select parent if child is selected
      const parent = findParentCategory(categories, category.category_id);
      if (parent && !isCategorySelected(newSelected, parent)) {
        newSelected = [
          ...newSelected,
          { category_id: parent.category_id, category_name: '', childCategoryList: [] }
        ];
      }
      setSelectedCategories(newSelected);
    } else {
      setSelectedCategories(selectedCategories.filter((c: any) => !allIds.includes(c.category_id)));
    }
  };

  // Check if all children are selected
  const isAllChildrenSelected = (category: any) => {
    if (!category.childCategoryList || category.childCategoryList.length === 0) return false;
    return category.childCategoryList.every((child: any) => isCategorySelected(selectedCategories, child));
  };

  // Toggle category expansion
  const toggleExpansion = (categoryId: number) => {
    setExpandedCategories(prev => ({ 
      ...prev, 
      [categoryId]: !prev[categoryId] 
    }));
  };

  // Render category item
  const renderCategoryItem = (category: any, level: number = 0) => {
    const isSelected = isCategorySelected(selectedCategories, category) || isAllChildrenSelected(category);
    const hasChildren = category.childCategoryList && category.childCategoryList.length > 0;
    const isExpanded = expandedCategories[category.category_id];
    const showSpecialTooltip = !hasChildren && category.is_main === false && category.isParent === false;
    
    return (
      <div key={category.category_id} className={`category-row level-${level}`}>
        <div className={`category-item ${isSelected ? 'selected' : ''}`}>
          {/* Indentation for hierarchy */}
          <div className="indentation" style={{ width: `${level * 20}px` }}>
            {level > 0 && <div className="indent-line"></div>}
          </div>

          {/* Expand/Collapse Button */}
          {hasChildren ? (
            <button
              type="button"
              className="expand-btn"
              onClick={() => toggleExpansion(category.category_id)}
            >
              {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
            </button>
          ) : (
            <div className="expand-spacer"></div>
          )}

          {/* Category Icon */}
          <div className="category-icon">
            {hasChildren ? (
              <FaFolder className="icon folder-icon" />
            ) : (
              <FaList className="icon list-icon" />
            )}
          </div>

          {/* Category Name */}
          <span className="category-name">{category.category_name}</span>

          {/* Invisible Category Icon for special categories */}
          {showSpecialTooltip && (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id={`tooltip-${category.category_id}`}>
                  Esta categoría no se mostrará en el menú online ni impreso, solo al tomar pedidos.
                </Tooltip>
              }
            >
              <span className="info-icon">
                <FaEyeSlash />
              </span>
            </OverlayTrigger>
          )}

          {/* Children count */}
          {hasChildren && (
            <span className="children-count">
              ({category.childCategoryList.length})
            </span>
          )}

          {/* Selection Checkbox */}
          <div className="checkbox-container">
            <input
              type="checkbox"
              id={`category-${category.category_id}`}
              checked={isSelected}
              onChange={(e) => handleCategoryChange(category, e.target.checked)}
            />
            <label htmlFor={`category-${category.category_id}`} className="checkbox-label"></label>
          </div>
        </div>

        {/* Children Categories */}
        {hasChildren && isExpanded && (
          <div className="children-list">
            {category.childCategoryList.map((child: any) => 
              renderCategoryItem(child, level + 1)
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="category-selection-container">
      {/* Compact Header */}
      <div className="selection-info">
        <div className="explanation">
          <p>Las categorías mantienen su estructura jerárquica en todos los menús.</p>
        </div>
        <div className="legend">
          <span className="legend-item">
            <FaFolder className="legend-icon folder" /> Principal
          </span>
          <span className="legend-item">
            <FaList className="legend-icon list" /> Con Items
          </span>
          <span className="legend-item">
            <FaEyeSlash className="legend-icon invisible" /> Invisible al cliente
          </span>
          {selectedCategories.length > 0 && (
            <span className="selection-count">
              {selectedCategories.length} seleccionada{selectedCategories.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Compact Categories List */}
      <div className="categories-list">
        {categories.length === 0 ? (
          <div className="empty-state">
            <FaFolder className="empty-icon" />
            <p>No hay categorías disponibles</p>
          </div>
        ) : (
          categories.map(category => renderCategoryItem(category))
        )}
      </div>
    </div>
  );
}

export default DisplaySelectionCategory